import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import DOMPurify from "dompurify";
import { marked } from "marked";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChatInput, LoadingBlocks, ThinkingDots } from "../../..";
import { useAppSelector } from "../../../../redux/hooks";
import { selectOrg } from "../../../../redux/slices/orgSlice";
import { selectPlayers } from "../../../../redux/slices/playerSlice";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  streamError?: { message: string; suggestion: string };
}

interface ChatConversationProps {
  messages: Message[];
  inputValue: string;
  setInputValue: (value: string) => void;
  onSendMessage: () => void;
  onVoiceTranscript: (transcript: string) => void;
  dataset: {
    players: string[];
    movementTypeId: string;
    files: File[];
  };
  isLoading?: boolean;
  onSelectedReportChange?: (reportHtml: string | null) => void;
}

interface Version {
  id: string;
  userMessage: Message;
  assistantMessage: Message;
  versionNumber: number;
  isHtmlReport: boolean;
}

// Render assistant text (a mix of markdown and plain text from the model) to HTML
// so formatting symbols (**, #, -, tables) display as formatting instead of literals.
// DOMPurify with the default config strips scripts/event handlers — required before
// injecting LLM-influenced content via dangerouslySetInnerHTML (frontend XSS rules).
const renderMarkdown = (content: string): string =>
  DOMPurify.sanitize(
    // gfm: tables/strikethrough; breaks: single newline -> <br> so plain-text
    // replies keep their line breaks (matching the prior segment-per-line display).
    marked.parse(content.trim(), { async: false, gfm: true, breaks: true }),
  );

// Typographic spacing for the rendered markdown. Restores the breathing room the
// prior segment-per-line display had (which browser-default margins don't give) via
// child-variant utilities, so paragraphs, lists, and headings read as neatly spaced.
const MARKDOWN_STYLES = [
  "leading-relaxed",
  "[&_p]:mb-3 [&_p:last-child]:mb-0",
  "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5",
  "[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5",
  "[&_li]:leading-relaxed",
  "[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2",
  "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2",
  "[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1",
  "[&_strong]:font-semibold",
  "[&_a]:underline [&_a]:text-orange-400",
  "[&_code]:rounded [&_code]:bg-white/10 [&_code]:px-1 [&_code]:text-[0.85em]",
  "[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-white/10 [&_pre]:p-3",
  "[&_table]:my-3 [&_table]:w-full [&_th]:text-left [&_th]:pr-4 [&_td]:pr-4 [&_td]:align-top",
].join(" ");

/** Assistant chat text rendered as sanitized markdown. User messages stay plain text. */
const MarkdownMessage: React.FC<{
  content: string;
  className?: string;
  dataTestId?: string;
}> = ({ content, className, dataTestId }) => (
  <div
    data-testid={dataTestId}
    className={`rl-markdown text-sm ${MARKDOWN_STYLES} ${className ?? ""}`}
    dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
  />
);

/**
 * Extract the report HTML from an assistant message for the report panel.
 * Handles: (1) raw HTML at start of message, (2) HTML inside ```html ... ``` —
 * we extract up to </html> so inner ``` (e.g. in <pre>) doesn't truncate,
 * with fallback to first ```, (3) mixed prose + HTML — take from <!DOCTYPE/html to </html>.
 */
const extractReportHtml = (content: string): string => {
  const trimmed = content.trim();
  if (!trimmed) return trimmed;

  // Already a full document at the start: return up to closing </html>
  if (
    trimmed.toLowerCase().startsWith("<!doctype html") ||
    trimmed.toLowerCase().startsWith("<html")
  ) {
    const closeTag = /<\/html\s*>/i.exec(trimmed);
    return closeTag
      ? trimmed.slice(0, closeTag.index + closeTag[0].length)
      : trimmed;
  }

  // Markdown code block: prefer extracting to </html> so we don't cut off on inner ```
  const codeBlockStart = trimmed.search(/```(?:html)?\s*\n?/i);
  if (codeBlockStart !== -1) {
    const afterFence = trimmed
      .slice(codeBlockStart)
      .replace(/^```(?:html)?\s*\n?/i, "");
    const closeHtml = /<\/html\s*>/i.exec(afterFence);
    if (closeHtml) {
      const inner = afterFence
        .slice(0, closeHtml.index + closeHtml[0].length)
        .trim();
      if (
        inner.toLowerCase().startsWith("<!doctype") ||
        inner.toLowerCase().startsWith("<html") ||
        /<div\s+class=|<h[1-6][^>]*>|<table[^>]*>/i.test(inner)
      ) {
        return inner;
      }
    }
    // Fallback: first ``` ... ``` (may truncate if HTML contains ```)
    const codeBlockMatch = trimmed.match(/```(?:html)?\s*([\s\S]*?)```/i);
    if (codeBlockMatch) {
      const inner = codeBlockMatch[1].trim();
      if (
        inner.toLowerCase().startsWith("<!doctype") ||
        inner.toLowerCase().startsWith("<html") ||
        /<div\s+class=|<h[1-6][^>]*>|<table[^>]*>/i.test(inner)
      ) {
        return inner;
      }
    }
  }

  // Mixed content: find first full document block
  const htmlStart = trimmed.search(/<!doctype\s+html\s*>|<html\s/i);
  if (htmlStart !== -1) {
    const afterStart = trimmed.slice(htmlStart);
    const closeTag = /<\/html\s*>/i.exec(afterStart);
    return closeTag
      ? afterStart.slice(0, closeTag.index + closeTag[0].length)
      : afterStart;
  }

  return trimmed;
};

/**
 * Content to show in the chat for an HTML report version: keep the prose,
 * replace the report HTML block (code block or raw document) with a short
 * placeholder so the left panel doesn't repeat the full HTML.
 */
const getDisplayContentForReportVersion = (
  content: string,
  versionNumber: number,
): string => {
  const trimmed = content.trim();
  if (!trimmed) return trimmed;
  const placeholder = `[HTML report generated — view Version ${versionNumber} in the panel on the right.]`;
  const withoutCodeBlock = trimmed.replace(
    /```(?:html)?\s*[\s\S]*?```/gi,
    (match) => {
      const inner = match
        .replace(/^```(?:html)?\s*/i, "")
        .replace(/```\s*$/, "");
      if (
        inner.trim().toLowerCase().startsWith("<!doctype") ||
        inner.trim().toLowerCase().startsWith("<html") ||
        /<div\s+class=|<h[1-6][^>]*>|<table[^>]*>/i.test(inner)
      ) {
        return placeholder;
      }
      return match;
    },
  );
  // If raw HTML document is still present, replace it with placeholder too
  const htmlStart = withoutCodeBlock.search(/<!doctype\s+html\s*>|<html\s/i);
  if (htmlStart !== -1) {
    const before = withoutCodeBlock.slice(0, htmlStart).trimEnd();
    const afterStart = withoutCodeBlock.slice(htmlStart);
    const closeTag = /<\/html\s*>/i.exec(afterStart);
    const afterHtml = closeTag
      ? afterStart.slice(closeTag.index + closeTag[0].length).trim()
      : "";
    return [before, placeholder, afterHtml].filter(Boolean).join("\n\n");
  }
  return withoutCodeBlock;
};

/** Banner shown when the stream failed (connection/timeout) so the user sees the error prominently */
const StreamErrorBanner: React.FC<{
  message: string;
  suggestion: string;
  dataTestId?: string;
}> = ({ message, suggestion, dataTestId }) => (
  <div
    data-testid={dataTestId ?? "chat-conversation-stream-error-banner"}
    className="flex gap-3 rounded-[4px] border border-red-500/50 bg-red-950/40 px-4 py-3"
  >
    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-red-200">{message}</p>
      <p className="text-sm text-red-200/80">{suggestion}</p>
    </div>
  </div>
);

/** Whether the message content looks like an HTML report (full doc or styled fragments). */
const isStyledReport = (content: string): boolean => {
  const trimmed = content.trim().toLowerCase();
  if (
    trimmed.startsWith("<!doctype html") ||
    trimmed.startsWith("<html") ||
    trimmed.includes("<html>") ||
    trimmed.includes("<head>") ||
    trimmed.includes("<body>") ||
    trimmed.includes("<style>") ||
    trimmed.includes("<style ")
  ) {
    return true;
  }
  const htmlPatterns = [
    /<div\s+class=/i,
    /<div\s+id=/i,
    /<h[1-6][^>]*>/i,
    /<table[^>]*>/i,
    /<section[^>]*>/i,
    /<article[^>]*>/i,
    /<header[^>]*>/i,
    /<footer[^>]*>/i,
  ];

  return htmlPatterns.some((pattern) => pattern.test(content));
};

/**
 * Sanitize HTML for the report iframe while keeping <style> and <script> contents.
 * We preserve <script> so Chart.js and inline chart configs can run (AI-generated reports).
 * DOMPurify strips scripts even with ADD_TAGS, so we: (1) pull out style and script blocks,
 * (2) sanitize the rest, (3) put the original blocks back.
 */
const STYLE_SLOT = '<span class="report-style-slot"></span>';
const SCRIPT_SLOT = '<span class="report-script-slot"></span>';

/** Escape for safe use in HTML attribute (prevents breaking out of quotes). */
const escapeAttr = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Escape </script> in script body so it doesn't close the tag prematurely. */
const escapeScriptBody = (s: string): string =>
  s.replace(/<\/script>/gi, () => "\\u" + "003c/script>");

const sanitizeHtml = (html: string): string => {
  const styleBlocks: string[] = [];
  const scriptBlocks: Array<{ src: string; inner: string }> = [];
  let withPlaceholders = html.replace(
    /<style[^>]*>([\s\S]*?)<\/style>/gi,
    (_, inner) => {
      styleBlocks.push(inner);
      return STYLE_SLOT;
    },
  );
  withPlaceholders = withPlaceholders.replace(
    /<script(?:\s[^>]*)?>([\s\S]*?)<\/script>/gi,
    (match, inner) => {
      const srcMatch = match.match(/src\s*=\s*["']([^"']+)["']/i);
      let src = srcMatch ? srcMatch[1] : "";
      // Normalize: AI sometimes outputs URLs with stray spaces (e.g. "cdn.jsdelivr. net")
      src = src.trim().replace(/\s+/g, "");
      scriptBlocks.push({ src, inner: inner ?? "" });
      return SCRIPT_SLOT;
    },
  );
  const sanitized = DOMPurify.sanitize(withPlaceholders, {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ["style"],
    ADD_ATTR: ["class", "id", "style"],
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
  });
  let styleIdx = 0;
  let scriptIdx = 0;
  return sanitized
    .replace(/<span[^>]*class="report-style-slot"[^>]*>\s*<\/span>/gi, () => {
      const block = styleBlocks[styleIdx++] ?? "";
      return block ? ["<style>", block, "</style>"].join("") : "";
    })
    .replace(/<span[^>]*class="report-script-slot"[^>]*>\s*<\/span>/gi, () => {
      const block = scriptBlocks[scriptIdx++];
      if (!block) return "";
      const srcAttr = block.src ? ` src="${escapeAttr(block.src)}"` : "";
      const body = escapeScriptBody(block.inner);
      const closeTag = "</scr" + "ipt>";
      return ["<script", srcAttr, ">", body, closeTag].join("");
    });
};

/**
 * Turn extracted content into a full document for the report iframe.
 * If it's already a full HTML document, sanitize and return. Otherwise
 * pull out any <style> blocks, wrap body content in a document shell,
 * and inject the styles so the report renders with CSS.
 */
const convertToHtml = (content: string): string => {
  const trimmed = content.trim();
  if (
    trimmed.toLowerCase().startsWith("<!doctype html") ||
    trimmed.toLowerCase().startsWith("<html")
  ) {
    return sanitizeHtml(trimmed);
  }
  const styleMatch = content.match(/<style[\s\S]*?<\/style>/gi);
  const styleBlock = styleMatch ? styleMatch.join("\n") : "";
  const contentWithoutStyle = content.replace(/<style[\s\S]*?<\/style>/gi, "");
  const isHtmlContent =
    /<div\s+class=/i.test(contentWithoutStyle) ||
    /<h[1-6][^>]*>/i.test(contentWithoutStyle) ||
    /<table[^>]*>/i.test(contentWithoutStyle);
  const htmlContent = isHtmlContent
    ? contentWithoutStyle
    : marked.parse(contentWithoutStyle, { async: false });
  const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      line-height: 1.6;
      color: #333;
      padding: 20px;
      margin: 0;
    }
  </style>
  ${styleBlock}
</head>
<body>
  ${htmlContent}
</body>
</html>`;

  return sanitizeHtml(fullHtml);
};

export const ChatConversation: React.FC<ChatConversationProps> = ({
  messages,
  inputValue,
  setInputValue,
  onSendMessage,
  onVoiceTranscript,
  dataset,
  isLoading = false,
  onSelectedReportChange,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);
  const players = useAppSelector(selectPlayers);
  const org = useAppSelector(selectOrg);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );
  const movementTypeName = org.details.movementTypes.find(
    (mt: any) => mt.id === Number(dataset.movementTypeId),
  )?.name;
  // Track request count to alternate thinking text between requests
  const requestCountRef = useRef(0);
  const [thinkingText, setThinkingText] = useState<"Thinking" | "Working">(
    "Thinking",
  );
  // Show thinking text when stream has paused (no new content for ~2s); logic kept for pause detection
  const [showAnalyzingIndicator, setShowAnalyzingIndicator] = useState(false);
  const pauseCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Alternate between "Thinking" and "Working" each time a new request starts
  useEffect(() => {
    if (isLoading) {
      requestCountRef.current += 1;
      setThinkingText(
        requestCountRef.current % 2 === 1 ? "Thinking" : "Working",
      );
    }
  }, [isLoading]);

  // Build versions from messages by pairing each user message with the *immediately following* assistant message.
  // This ensures correct pairing when there are "orphan" user messages (e.g. imported chat ending with user but no assistant).
  const versions: Version[] = [];
  const pendingUserMessages: Message[] = [];
  let streamingAssistantMessage: Message | null = null;

  // Track version number only for HTML reports
  let reportVersionNumber = 0;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg.role !== "user") continue;

    const nextMsg = messages[i + 1];
    const nextIsAssistant = nextMsg?.role === "assistant";
    const isCurrentlyStreaming =
      nextIsAssistant && isLoading && i + 1 === messages.length - 1;
    const isComplete =
      nextIsAssistant && nextMsg.content.length > 0 && !isCurrentlyStreaming;

    if (isComplete) {
      const reportContent = extractReportHtml(nextMsg.content);
      const isReport = isStyledReport(reportContent);
      if (isReport) {
        reportVersionNumber++;
      }
      versions.push({
        id: `version-${versions.length + 1}`,
        userMessage: msg,
        assistantMessage: nextMsg,
        versionNumber: isReport ? reportVersionNumber : 0,
        isHtmlReport: isReport,
      });
      i++; // Skip the assistant we consumed
    } else if (isCurrentlyStreaming) {
      pendingUserMessages.push(msg);
      streamingAssistantMessage = nextMsg;
      i++; // Skip the streaming assistant
    } else {
      // No assistant yet (orphan user message)
      pendingUserMessages.push(msg);
    }
  }

  // Show thinking indicator only after ~2s with no new streamed content (pause in stream)
  useEffect(() => {
    if (pauseCheckTimeoutRef.current) {
      clearTimeout(pauseCheckTimeoutRef.current);
      pauseCheckTimeoutRef.current = null;
    }
    if (!isLoading || !streamingAssistantMessage?.content) {
      setShowAnalyzingIndicator(false);
      return;
    }
    setShowAnalyzingIndicator(false);
    pauseCheckTimeoutRef.current = setTimeout(() => {
      setShowAnalyzingIndicator(true);
      pauseCheckTimeoutRef.current = null;
    }, 2000);
    return () => {
      if (pauseCheckTimeoutRef.current) {
        clearTimeout(pauseCheckTimeoutRef.current);
      }
    };
  }, [isLoading, streamingAssistantMessage?.content]);

  // Get only HTML report versions for the right panel
  const htmlReportVersions = versions.filter((v) => v.isHtmlReport);

  // Auto-select the latest HTML report version when a new one is added
  useEffect(() => {
    if (htmlReportVersions.length > 0) {
      const latestReport = htmlReportVersions[htmlReportVersions.length - 1];
      setSelectedVersionId(latestReport.id);
    }
    // We intentionally only depend on htmlReportVersions.length to avoid selecting
    // a new version when content updates during streaming
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlReportVersions.length]);

  // Get the currently selected version (must be an HTML report)
  const selectedVersion = htmlReportVersions.find(
    (v) => v.id === selectedVersionId,
  );

  // Convert selected version content to renderable HTML (extract from code blocks if needed)
  const selectedVersionHtml = useMemo(() => {
    if (!selectedVersion) return "";
    return convertToHtml(
      extractReportHtml(selectedVersion.assistantMessage.content),
    );
  }, [selectedVersion]);

  // Notify parent when selected report changes
  useEffect(() => {
    onSelectedReportChange?.(selectedVersionHtml || null);
  }, [selectedVersionHtml, onSelectedReportChange]);

  // Auto-scroll to bottom when messages change (e.g. new message or streamed content)
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (inputValue.trim()) {
      onSendMessage();
    }
  };

  const isDatasetComplete =
    dataset.players.length > 0 && dataset.movementTypeId !== "";

  // Get player name for display
  const getPlayerName = () => {
    if (dataset.players.length === 0) return "";
    const player = players.playerList.find(
      (p) => p.orgPlayerId === dataset.players[0],
    );
    return player ? `${player.firstName} ${player.lastName}` : "";
  };

  return (
    <div
      data-testid="reboot-live-chat-conversation-interface"
      className="flex-1 flex overflow-hidden bg-[#0b0b0b]"
    >
      {/* Chat Panel - Left Side */}
      <div
        data-testid="chat-conversation-panel"
        className="flex flex-col w-1/3 border-r border-[#424245]"
      >
        {/* Player/Session Header */}
        <div data-testid="chat-conversation-header" className="px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3
                data-testid="chat-conversation-player-name"
                className="font-['Bebas_Neue'] uppercase font-normal text-white text-xl leading-[26px]"
              >
                {getPlayerName()}
              </h3>
              <p
                data-testid="chat-conversation-movement-type"
                className="font-normal text-sm text-white mt-1 leading-[26px]"
              >
                Movement Type: {movementTypeName}
              </p>
            </div>
          </div>
        </div>

        {/* Conversation Thread */}
        <div
          data-testid="chat-conversation-versions-container"
          className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        >
          {/* Interleaved user messages and responses */}
          {versions.map((version) => {
            const isSelected = version.id === selectedVersionId;

            return (
              <div key={version.id} className="space-y-4">
                {/* User Message */}
                <div className="flex justify-end">
                  <div
                    data-testid={`chat-conversation-user-message-${version.id}`}
                    className="max-w-[85%] rounded-[4px] px-3 py-2 bg-[#424245] text-white"
                  >
                    <p className="text-sm whitespace-pre-wrap">
                      {version.userMessage.content}
                    </p>
                  </div>
                </div>

                {version.isHtmlReport ? (
                  /* HTML report: show streaming messages (prose only) + version card */
                  <div className="space-y-3">
                    {/* Prose from the assistant message (always white for initial and follow-up) */}
                    <div className="space-y-2 text-[#f7f7f9]">
                      <MarkdownMessage
                        content={getDisplayContentForReportVersion(
                          version.assistantMessage.content,
                          version.versionNumber,
                        )}
                        dataTestId={`chat-conversation-report-prose-${version.id}`}
                      />
                    </div>
                    <button
                      data-testid={`chat-conversation-version-${version.versionNumber}`}
                      onClick={() =>
                        !isLoading && setSelectedVersionId(version.id)
                      }
                      disabled={isLoading}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-[4px] transition-colors border ${
                        isLoading
                          ? "opacity-50 cursor-not-allowed border-white/30"
                          : isSelected
                            ? "border-[#07FB5C] bg-transparent"
                            : "border-white/50 bg-transparent hover:border-white"
                      }`}
                    >
                      <span
                        className={`text-sm font-medium ${
                          isSelected ? "text-[#07FB5C]" : "text-white"
                        }`}
                      >
                        Version {version.versionNumber}
                      </span>
                      {isSelected ? (
                        <CheckCircleIcon className="w-5 h-5 text-[#07FB5C]" />
                      ) : (
                        <ArrowUturnLeftIcon className="w-5 h-5 text-secondary-900" />
                      )}
                    </button>

                    {/* Follow up question prompt */}
                    <p className="text-[#B4AFAF] text-sm">
                      Follow up question?
                    </p>
                  </div>
                ) : (
                  /* Inline text response (for non-HTML responses) */
                  <div
                    data-testid={`chat-conversation-text-response-${version.id}`}
                    className="space-y-2 text-[#f7f7f9]"
                  >
                    <MarkdownMessage
                      content={version.assistantMessage.content}
                    />

                    {/* Follow up question prompt */}
                    <p className="text-[#B4AFAF] text-sm pt-1">
                      Follow up question?
                    </p>
                  </div>
                )}
                {version.assistantMessage.streamError && (
                  <StreamErrorBanner
                    message={version.assistantMessage.streamError.message}
                    suggestion={version.assistantMessage.streamError.suggestion}
                    dataTestId={`chat-conversation-stream-error-${version.id}`}
                  />
                )}
              </div>
            );
          })}

          {/* Pending user messages (waiting for response or orphaned) */}
          {pendingUserMessages.map((pendingMsg, idx) => (
            <div key={`pending-${pendingMsg.id}`} className="flex justify-end">
              <div
                data-testid={
                  idx === 0
                    ? "chat-conversation-pending-user-message"
                    : `chat-conversation-pending-user-message-${idx}`
                }
                className="max-w-[85%] rounded-[4px] px-3 py-2 bg-[#424245] text-white"
              >
                <p className="text-sm whitespace-pre-wrap">
                  {pendingMsg.content}
                </p>
              </div>
            </div>
          ))}

          {/* Streaming assistant message: gray while streaming, white when complete */}
          {streamingAssistantMessage && (
            <>
              <div
                data-testid="chat-conversation-streaming-response"
                className="space-y-2 text-[#f7f7f9]"
              >
                {/* Render partial markdown live as chunks arrive; marked tolerates
                    incomplete markdown, so the formatting resolves as the stream fills in. */}
                <MarkdownMessage content={streamingAssistantMessage.content} />
              </div>
              {streamingAssistantMessage.streamError && (
                <StreamErrorBanner
                  message={streamingAssistantMessage.streamError.message}
                  suggestion={streamingAssistantMessage.streamError.suggestion}
                  dataTestId="chat-conversation-streaming-error-banner"
                />
              )}
            </>
          )}

          {/* Thinking indicator when loading and no streamed content yet */}
          {isLoading && !streamingAssistantMessage?.content && (
            <div
              data-testid="chat-conversation-thinking-indicator"
              className="flex justify-start"
            >
              <div className="rounded px-4 py-2">
                <div className="flex gap-2 items-center">
                  <ThinkingDots />
                  <p className="text-base text-white">{thinkingText}...</p>
                </div>
              </div>
            </div>
          )}

          {/* Pause indicator: show thinking text when stream has paused while the backend processes the request */}
          {isLoading && showAnalyzingIndicator && (
            <div
              data-testid="chat-conversation-working-indicator"
              className="flex justify-start"
            >
              <div className="rounded px-4 py-2">
                <div className="flex gap-2 items-center">
                  <ThinkingDots />
                  <p className="text-base text-white">{thinkingText}...</p>
                </div>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Chat Input */}
        <div data-testid="chat-conversation-input-container" className="p-4">
          <ChatInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSend={handleSend}
            onVoiceTranscript={onVoiceTranscript}
            disabled={!isDatasetComplete}
            sendDisabled={isLoading}
            placeholder="Ask questions about the report, request changes, or explore the data further..."
          />
        </div>
      </div>

      {/* Report Panel - Right Side */}
      <div
        data-testid="chat-conversation-preview-panel"
        className={`w-2/3 flex flex-col ${
          selectedVersion && !isLoading ? "bg-white" : "bg-[#1d1d1f]"
        }`}
      >
        {isLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            {/* Loading Blocks - show until streaming is complete */}
            <div
              data-testid="chat-conversation-preview-loading"
              className="mb-6 w-full max-w-[600px]"
            >
              <LoadingBlocks />
            </div>

            {/* Status Message */}
            <p
              data-testid="chat-conversation-building-report-message"
              className="text-white text-base"
            >
              Building your report...
            </p>
          </div>
        ) : selectedVersion ? (
          <div
            data-testid="chat-conversation-preview-content"
            className="flex-1 w-full h-full"
          >
            {/* Render report content in an iframe for proper styling */}
            {/* allow-scripts: AI-generated reports include Chart.js and inline chart configs; without it charts render blank. */}
            <iframe
              data-testid={`chat-conversation-preview-version-${selectedVersion.versionNumber}`}
              srcDoc={selectedVersionHtml}
              className="w-full h-full border-none"
              title={`Report Version ${selectedVersion.versionNumber}`}
              sandbox="allow-scripts"
            />
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            {/* Placeholder - no HTML reports yet */}
            <div
              data-testid="chat-conversation-preview-placeholder"
              className="text-center"
            >
              <p className="text-white text-base mb-2">
                {versions.length > 0
                  ? "No report generated yet"
                  : "Waiting for report..."}
              </p>
              <p className="text-gray-400 text-sm">
                {versions.length > 0
                  ? "Ask a follow-up question to generate a report"
                  : "Send a message to get started"}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
